import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { canonicalLink, geoMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";

const TITLE = "All Products — Awnings, Shade, Outdoor Living & Covers | Texas Made Shade";
const DESCRIPTION =
  "Every product Texas Made Shade builds and installs: custom awnings, retractable awnings, motorized screens, louvered pergolas, sail shades, patio covers, marine and trailer covers, welding and fabrication — San Antonio, Austin and across Texas.";

export const Route = createFileRoute("/products")({
  head: () => ({
    links: [canonicalLink("/products")],
    meta: [
      ...geoMeta(),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://texasmadeshade.com/products" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: pageHeadScripts({
      path: "/products",
      name: TITLE,
      description: DESCRIPTION,
    }),
  }),
  component: ProductsPage,
});

type Group = {
  heading: string;
  blurb: string;
  items: { to: string; label: string }[];
};

const GROUPS: Group[] = [
  {
    heading: "Awnings",
    blurb:
      "Fabric and metal awnings fabricated in our Seguin shop, plus factory-backed Solair® retractable systems.",
    items: [
      { to: "/custom-awnings", label: "Custom Awnings" },
      { to: "/residential-awnings", label: "Residential Awnings" },
      { to: "/commercial-awnings", label: "Commercial Awnings" },
      { to: "/restaurant-awnings", label: "Restaurant Awnings" },
      { to: "/retractable-awnings", label: "Solair® Retractable Awnings" },
      { to: "/metal-awnings", label: "Metal Awnings" },
      { to: "/fabric-awnings", label: "Fabric Awnings" },
      { to: "/backlit-awnings", label: "Backlit Awnings" },
      { to: "/recover-awnings", label: "Awning & Canopy Recovers" },
    ],
  },
  {
    heading: "Outdoor Living",
    blurb:
      "Motorized shade, pergolas, lighting and cooling that turn a hot patio into usable living space.",
    items: [
      { to: "/louvered-pergolas", label: "Motorized Louvered Pergolas" },
      { to: "/exterior-vertical-shades", label: "Motorized Exterior Screens" },
      { to: "/curtains", label: "Outdoor Curtains & Drop Panels" },
      { to: "/gazebo-privacy-panels", label: "Gazebo Covers & Privacy Panels" },
      { to: "/outdoor-lighting", label: "Outdoor Lighting" },
      { to: "/outdoor-hvac", label: "Outdoor Heating & Cooling" },
      { to: "/outdoor-cooling-misting", label: "Outdoor Misting Cooling" },
      { to: "/lone-star-elite", label: "Smart Automation" },
      { to: "/hill-country-series", label: "Outdoor Living Overview" },
    ],
  },
  {
    heading: "Shade Structures",
    blurb: "Engineered sail shades, canopies and covers for homes, schools, parks and businesses.",
    items: [
      { to: "/sail-shades", label: "Sail Shades & Tension Structures" },
      { to: "/pergola-covers", label: "Pergola & Gazebo Covers" },
      { to: "/patio-covers", label: "Patio Covers" },
      { to: "/pool-spa", label: "Pool & Spa Shade" },
      { to: "/covered-parking", label: "Covered Parking" },
      { to: "/playground-shade", label: "Playground Shade" },
      { to: "/school-shade-structures", label: "School Shade Structures" },
      { to: "/church-shade-structures", label: "Church Shade Structures" },
      { to: "/car-wash-shade-sails", label: "Car Wash Shade Sails" },
    ],
  },
  {
    heading: "Covers & Canvas",
    blurb: "Patterned, sewn and fitted covers for boats, RVs, trailers, ag equipment and gear.",
    items: [
      { to: "/boat-covers", label: "Boat Covers & Marine Canvas" },
      { to: "/rv-covers", label: "RV Covers" },
      { to: "/trailer-covers", label: "Trailer Covers" },
      { to: "/stock-trailer-covers", label: "Stock Trailer Covers" },
      { to: "/wagon-covers", label: "Wagon Covers" },
      { to: "/utv-atv-covers", label: "UTV & ATV Covers" },
      { to: "/hunting-fishing-covers", label: "Hunting & Fishing Canvas" },
      { to: "/deer-blind-covers", label: "Deer Blind Covers" },
      { to: "/cooler-gun-gear-covers", label: "Cooler, Gun & Gear Covers" },
    ],
  },
  {
    heading: "Welding & Fabrication",
    blurb: "Aluminum and steel fabrication, frames, truck builds and trailer work in-house.",
    items: [
      { to: "/custom-fabrication", label: "Welding & Custom Fabrication" },
      { to: "/dog-boxes", label: "Dog Boxes & Hunting Truck Builds" },
      { to: "/hog-traps-and-cages", label: "Hog Traps & Cages" },
      { to: "/trailer-wiring", label: "Trailer Wiring" },
    ],
  },
  {
    heading: "Service, Care & Materials",
    blurb: "Recovers, repairs, maintenance memberships and the fabrics we build with.",
    items: [
      { to: "/revival-series", label: "Service & Recovery" },
      { to: "/ranger-care", label: "Ranger Care Memberships" },
      { to: "/fabrics", label: "Outdoor Fabrics" },
      { to: "/shop", label: "Shop Supplies & Sunbrella® Lifestyle" },
    ],
  },
];

function ProductsPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
        <section className="border-b-8 border-navy-deep bg-navy-deep py-14 text-white lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-red">
              Shade • Outdoor Living • Fabrication
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-tight md:text-6xl">
              Everything We Build
            </h1>
            <p className="mt-4 max-w-3xl text-base text-white/85 md:text-lg">
              Custom shade, motorized outdoor living, commercial awnings and specialty covers —
              designed, installed and serviced by a real Texas shop in Seguin, working across San
              Antonio, Austin, the Hill Country and the Gulf Coast.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/estimate-quotes"
                className="inline-flex items-center gap-2 bg-red px-6 py-3.5 font-display text-base uppercase tracking-wider text-white shadow-bold transition hover:bg-red-deep"
              >
                <ArrowRight className="h-5 w-5" /> Get a Free Estimate
              </Link>
              <a
                href="tel:+18303909588"
                className="inline-flex items-center gap-2 bg-white px-6 py-3.5 font-display text-base uppercase tracking-wider text-navy-deep shadow-bold transition hover:bg-cream"
              >
                Call +1 (830) 390-9588
              </a>
            </div>
          </div>
        </section>

        {GROUPS.map((g) => (
          <section key={g.heading} className="border-b-2 border-navy-deep/15 py-12 lg:py-14">
            <div className="mx-auto max-w-7xl px-4 lg:px-8">
              <h2 className="font-display text-2xl font-bold uppercase text-navy-deep md:text-3xl">
                {g.heading}
              </h2>
              <p className="mt-2 max-w-3xl text-base text-navy-deep/75">{g.blurb}</p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {g.items.map((it) => (
                  <li key={it.to + it.label}>
                    <Link
                      to={it.to}
                      className="group flex items-center justify-between gap-3 border-2 border-navy-deep bg-white px-4 py-3 font-display text-sm uppercase tracking-wide text-navy-deep shadow-card transition hover:bg-navy-deep hover:text-cream"
                    >
                      {it.label}
                      <ArrowRight className="h-4 w-4 shrink-0 text-red group-hover:text-white" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}

        <section className="py-14">
          <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
            <h2 className="font-display text-2xl font-bold uppercase text-navy-deep md:text-3xl">
              Not sure which one you need?
            </h2>
            <p className="mt-3 text-base text-navy-deep/75">
              Tell us about the space and we'll recommend the right system, measure on site and
              quote it — no charge.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/estimate-quotes"
                className="inline-flex items-center gap-2 bg-red px-6 py-3.5 font-display text-sm uppercase tracking-wider text-white shadow-bold transition hover:bg-red-deep"
              >
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/series"
                className="inline-flex items-center gap-2 border-2 border-navy-deep px-6 py-3.5 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-cream"
              >
                Browse by Series
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
